import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import Icon from "@/components/Icon";
import Tag, { type TagType } from "@/components/Tag/Tag";
import { SkeletonList } from "@/components/Skeleton";
import { toUTCFormat } from "@/lib/dateHelpers";
import { useNavigate } from "@/router";
import type { GroupedTimelineMilestones, TimelineCategory, TimelineMilestone } from "../utils";

const tagTypes: Record<TimelineCategory, TagType> = {
  Membership: "primary",
  Section: "tertiary",
  Education: "information",
  Career: "success",
  Prospect: "warn"
};

function formatRange(milestone: TimelineMilestone) {
  const start = toUTCFormat(milestone.date);
  const end = milestone.endDate ? toUTCFormat(milestone.endDate) : null;

  if (start && end) {
    return `${start} - ${end}`;
  }

  return start || "Unknown date";
}

function TimelineCard({ milestone, isLast }: { milestone: TimelineMilestone; isLast: boolean }) {
  const navigate = useNavigate();

  return (
    <div className="relative flex gap-4 pb-6 last:pb-0" data-testid="timeline-milestone">
      {!isLast && <div className="absolute left-[19px] top-11 h-[calc(100%-44px)] w-px bg-sys-brand-outline-variant" />}
      <div className="z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sys-brand-primary-container-bright text-sys-brand-on-primary-container">
        <Icon name={milestone.iconName} size={22} />
      </div>
      <div className="min-w-0 flex-1 rounded-2xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-low p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="text-label-medium text-sys-brand-on-surface-variant">{formatRange(milestone)}</div>
            <h3 className="mt-1 text-title-medium text-sys-brand-on-surface">{milestone.title}</h3>
            {milestone.description && (
              <p className="mt-2 text-body-medium text-sys-brand-on-surface-variant">{milestone.description}</p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap gap-2 md:justify-end">
            <Tag tagType={tagTypes[milestone.category]} tagStyle="standard" size="small">
              {milestone.category}
            </Tag>
            {milestone.status && (
              <Tag tagType="surface" tagStyle="standard" size="small">
                {milestone.status}
              </Tag>
            )}
          </div>
        </div>
        {milestone.link && (
          <div className="mt-4">
            <Button variant="text" trailingIcon icon="arrow_forward" onClick={() => navigate(milestone.link as any)}>
              View details
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TimelineMilestones({
  groups,
  loading
}: {
  groups: GroupedTimelineMilestones[];
  loading?: boolean;
}) {
  if (loading) {
    return (
      <div className="py-6" data-testid="timeline-skeleton">
        <SkeletonList rows={8} />
      </div>
    );
  }

  if (!groups.length) {
    return (
      <EmptyState
        iconName="timeline"
        title="No Timeline Milestones Yet"
        caption="Milestones will appear here as membership, education, section, and career history becomes available."
      />
    );
  }

  return (
    <div className="flex flex-col gap-8" data-testid="person-timeline">
      {groups.map((group) => (
        <section key={group.year} aria-labelledby={`timeline-year-${group.year}`}>
          <h2 id={`timeline-year-${group.year}`} className="mb-4 text-headline-small-bold text-sys-brand-on-surface">
            {group.year}
          </h2>
          <div className="flex flex-col">
            {group.milestones.map((milestone, index) => (
              <TimelineCard key={milestone.id} milestone={milestone} isLast={index === group.milestones.length - 1} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
